import Divider from "antd/lib/divider";
import { type ReactNode, useMemo, useState } from "react";

import { getResourceIRI, OrderingDirection, useClassroomListQuery, useClassroomQuery } from "@sol/sdk";
import { Select, type SelectProps } from "@sol/uikit/data-entry/Select";
import { Skeleton } from "@sol/uikit/feedback/Skeleton/Skeleton";
import { Pagination } from "@sol/uikit/navigation/Pagination/Pagination";

const RESULTS_PER_PAGE = 20;

const readSelectedClassroom = (value: SelectProps["value"]) => (typeof value === "string" ? value : undefined);

export const ClassroomSelect = ({ onSelect, onChange, value, ...rest }: SelectProps) => {
    const [search, setSearch] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const selectedClassroomIri = readSelectedClassroom(value);

    const { data, isLoading } = useClassroomListQuery({
        filters: {
            groups: ["classroom:read:minimal"],
            title: search || undefined,
        },
        pagination: { page: currentPage, perPage: RESULTS_PER_PAGE },
        order: { by: "title", direction: OrderingDirection.ASC },
    });

    // Keeps the selected classroom labelled even when the results moved past its page
    const { data: selectedClassroom } = useClassroomQuery(
        { classroom: selectedClassroomIri },
        { enabled: Boolean(selectedClassroomIri) },
    );

    const totalItems = data?.["hydra:totalItems"] ?? 0;

    const options = useMemo(() => {
        const pageOptions =
            data?.["hydra:member"].map(classroom => ({
                value: getResourceIRI(classroom),
                label: classroom.title,
            })) ?? [];

        if (!selectedClassroomIri || !selectedClassroom) {
            return pageOptions;
        }

        return [
            { value: getResourceIRI(selectedClassroom), label: selectedClassroom.title },
            ...pageOptions.filter(option => option.value !== selectedClassroomIri),
        ];
    }, [data, selectedClassroom, selectedClassroomIri]);

    const searchClassrooms = (nextSearch: string) => {
        setSearch(nextSearch);
        setCurrentPage(1);
    };

    return (
        <Select
            {...rest}
            showSearch
            filterOption={false}
            searchValue={search}
            onSearch={searchClassrooms}
            value={value}
            loading={isLoading}
            notFoundContent={isLoading ? <Skeleton title={false} /> : undefined}
            onChange={onChange ?? onSelect}
            popupMatchSelectWidth={false}
            options={options}
            popupRender={(menu: ReactNode) => (
                <>
                    {menu}
                    {totalItems > RESULTS_PER_PAGE ? (
                        <>
                            <Divider className="my-ant" />
                            <Pagination
                                className="mb-ant"
                                current={currentPage}
                                total={totalItems}
                                pageSize={RESULTS_PER_PAGE}
                                onChange={setCurrentPage}
                                size="small"
                                align="center"
                                hideOnSinglePage
                                showSizeChanger={false}
                                simple
                            />
                        </>
                    ) : null}
                </>
            )}
        />
    );
};
